import {
    ApolloClient,
    InMemoryCache,
    HttpLink,
    ApolloLink,
    from,
    GraphQLRequest,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { onError } from '@apollo/client/link/error';
import fetch from 'cross-fetch';
import { GRAPHQL_CLIENT_NAMES } from 'lib/types';
import { trackWithGoogleAnalytics4 } from 'analytics/ga';

const batchLinkOptions = {
    batchInterval: 100,
    batchMax: 20,
};

const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    const operationName = operation.operationName;
    const variables = operation.variables;

    const isCreateAppointmentError = operationName === 'createAppointment';

    // don't track "Time taken" errors
    const isTimeTakenError =
        isCreateAppointmentError &&
        !!graphQLErrors?.find((error) => error.message === 'Time taken');

    if (isTimeTakenError) {
        return;
    }

    console.warn('unexpected error occurred in graphql call', {
        graphQLErrors,
        networkError,
        operation,
        operationName,
        variables,
        isCreateAppointmentError,
    });

    trackWithGoogleAnalytics4(false, 'event', 'exception', {
        // any create appointment error that reaches this code
        // is considered fatal
        fatal: isCreateAppointmentError,
        description: JSON.stringify({
            type: 'graphql',
            graphQLErrors,
            networkError,
            operationName,
            variables,
        }),
    });
});

const defaultLink = from([
    errorLink,
    new BatchHttpLink({
        uri: process.env.GATSBY_BACKEND_URL,
        fetch,
        ...batchLinkOptions,
    }),
]);

const creditCardsLink = new BatchHttpLink({
    uri: process.env.GATSBY_CREDIT_CARDS_API_URL,
    fetch,
    ...batchLinkOptions,
});

const newsletterLink = new HttpLink({
    uri: process.env.GATSBY_NEWSLETTER_API_URL,
    fetch,
});

const voglioLink = new BatchHttpLink({
    uri: process.env.GATSBY_VOGLIO_API_URL,
    fetch,
    ...batchLinkOptions,
});

const bookingLink = new BatchHttpLink({
    uri: process.env.GATSBY_BOOKING_API_URL,
    fetch,
    ...batchLinkOptions,
});

const promoCodesLink = new BatchHttpLink({
    uri: process.env.GATSBY_PROMO_CODES_API_URL,
    fetch,
    ...batchLinkOptions,
});

const authLink = new BatchHttpLink({
    uri: process.env.GATSBY_AUTH_API_URL,
    fetch,
    ...batchLinkOptions,
});

const newsLetterOrDefaultLink = ApolloLink.split(
    (operation) => {
        return (
            operation.getContext().clientName ===
            GRAPHQL_CLIENT_NAMES.newsletter
        );
    },
    newsletterLink,
    defaultLink,
);

const voglioOrOtherLink = ApolloLink.split(
    (operation) =>
        operation.getContext().clientName === GRAPHQL_CLIENT_NAMES.voglio,
    voglioLink,
    newsLetterOrDefaultLink,
);

const bookingOrOtherLink = ApolloLink.split(
    (operation) =>
        operation.getContext().clientName === GRAPHQL_CLIENT_NAMES.booking,
    bookingLink,
    voglioOrOtherLink,
);

const promoCodesOrOtherLink = ApolloLink.split(
    (operation) =>
        operation.getContext().clientName === GRAPHQL_CLIENT_NAMES.promoCodes,
    promoCodesLink,
    bookingOrOtherLink,
);

const creditCardsOrOtherLink = ApolloLink.split(
    (operation) => {
        return (
            operation.getContext().clientName ===
            GRAPHQL_CLIENT_NAMES.creditCards
        );
    },
    creditCardsLink,
    promoCodesOrOtherLink,
);

const authenticatedLink = setContext((request: GraphQLRequest, prevContext) => {
    if (prevContext?.authenticate) {
        return import('auth/lib/tokenStorage').then(
            ({ getAccessTokenFromSessionStorage }) => {
                return getAccessTokenFromSessionStorage().then(
                    (maybeAccessToken) => {
                        return {
                            ...prevContext,
                            headers: {
                                ...prevContext.headers,
                                Authorization: maybeAccessToken
                                    ? `Bearer ${maybeAccessToken}`
                                    : '',
                                'Apollo-Require-Preflight': 'true',
                            },
                        };
                    },
                );
            },
        );
    }

    return prevContext;
});

const authOrOtherLink = authenticatedLink.split(
    (operation) => {
        return operation.getContext().clientName === GRAPHQL_CLIENT_NAMES.auth;
    },
    authLink,
    creditCardsOrOtherLink,
);

export default new ApolloClient({
    cache: new InMemoryCache(),
    link: authOrOtherLink,
});

/*
const link = new HttpLink({ uri: process.env.GATSBY_BACKEND_URL, fetch });

export default new ApolloClient({
    cache: new InMemoryCache(),
    link,
});
 */
